import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { useParams } from 'react-router-dom';
import Loading from '@/components/Loading/Loading';
import Button from '@/components/UI/Button/Button';
import { type Study } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import {
  type GroupTarget,
  type PlannedAnimalTarget,
  type RegisteredAnimalTarget,
  type SampleDefinitionFormData,
} from '@/model/SamplePlan.model';
import { type Color } from '@/model/Common.model';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import { type TeamGlossary } from '@/model/Glosary.model';
import { type MetadataGlossaryV1 } from '@/model/Metadata.model';
import { _isNil, _notNil } from '@/littledash';
import { isWriteUserForStudy } from '@/support/auth';
import { useModalAction } from '@/utils/modal';
import { useAsyncExport } from '@/support/Hooks/api/useAsyncExport';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import { useSelector } from 'react-redux';
import { downloadSamplingPlan } from '@/components/Samples/Samples.utils';
import {
  SamplePlanTableBulkActionsBar,
  SamplePlanTableViewContent,
} from '@/components/Samples/SamplePlanTableViewContent';

const samplePlanViewFeatureSelector = createSelector(featuresSelector, (features) => ({
  asyncExportsEnabled: features.async_exports === true,
}));

export const SamplePlanTableView: FC = () => {
  const { id: studyId } = useParams();
  const { openModal } = useModalAction();
  const { asyncExportsEnabled } = useSelector(samplePlanViewFeatureSelector);
  const triggerAsyncExport = useAsyncExport();

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    options: { invokeOnInit: true, onError: { slug: 'sample-plans-study-load' } },
    query: { include: ['users'] },
  });
  const { response: groupsResponse, loading: groupsLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    query: { perPage: -1 },
  });
  const { response: sampleMetadataGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: { include_inactive: false, filter_type: 'sample_meta' },
    invokeOnInit: true,
    options: { onError: { slug: 'sample-meta-load' } },
  });
  const { response: sampleGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: { group: 'samples', perPage: -1, order: 'asc', sort: 'title' },
    options: { onError: { slug: 'samples-glossary-load' } },
  });

  const {
    loading: samplePlanLoading,
    response: samplePlanResponse,
    invoke: loadSamplePlan,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples',
    path: { studyApiId: 'sdy_invalid' },
    invokeOnInit: false,
    options: { onError: { slug: 'sample-plans-table-load-sample-plan' } },
  });
  const {
    loading: registeredAnimalsLoading,
    response: registeredAnimalsResponse,
    invoke: loadRegisteredAnimals,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: 'sdy_invalid' },
    query: { perPage: -1, include: ['study'] },
    invokeOnInit: false,
    options: { onError: { slug: 'sample-plans-table-load-registered-animals' } },
  });
  const {
    loading: plannedAnimalsLoading,
    response: plannedAnimalsResponse,
    invoke: loadPlannedAnimals,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    path: { studyApiId: 'sdy_invalid' },
    query: { perPage: -1 },
    invokeOnInit: false,
    options: { onError: { slug: 'sample-plans-table-load-planned-animals' } },
  });

  const { invoke: createSamplingPlanExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/sampling-plan',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'sample-plans-export' } },
  });

  useEffect(() => {
    if (studyResponse?.type === 'success') {
      const studyApiId = (studyResponse.body as { data: Study }).data.api_id;
      loadSamplePlan({ path: { studyApiId } });
      loadPlannedAnimals({ path: { studyApiId }, query: { perPage: -1 } });
      loadRegisteredAnimals({ path: { studyId: studyApiId }, query: { perPage: -1, include: ['study_group'] } });
    }
  }, [studyResponse]);

  const loadedStudyApiId = useMemo(
    () => (studyResponse?.type === 'success' ? (studyResponse.body as { data: Study }).data.api_id : undefined),
    [studyResponse]
  );

  const onSamplePlanUpdated = useCallback(() => {
    if (_notNil(loadedStudyApiId)) {
      loadSamplePlan({ path: { studyApiId: loadedStudyApiId } });
    }
  }, [loadedStudyApiId, loadSamplePlan]);

  const tableData = useMemo(() => {
    if (
      groupsResponse?.type !== 'success' ||
      registeredAnimalsResponse?.type !== 'success' ||
      plannedAnimalsResponse?.type !== 'success'
    ) {
      return null;
    }
    const groups = (groupsResponse.body as { data: Array<TreatmentGroup> }).data.map<GroupTarget>((group) => ({
      api_id: group.api_id as GroupApiId,
      name: group.name,
      no: group.no,
      color: group.color as Color,
      max_subjects: group.max_subjects,
    }));
    const registeredAnimals = registeredAnimalsResponse.body.data.map<RegisteredAnimalTarget>((animal) => ({
      api_id: animal.api_id,
      name: animal.name,
      study_group_api_id: animal.study_group?.api_id ?? null,
    }));
    return { groups, registeredAnimals };
  }, [groupsResponse, registeredAnimalsResponse, plannedAnimalsResponse]);

  if (
    studyLoading ||
    samplePlanLoading ||
    groupsLoading ||
    registeredAnimalsLoading ||
    plannedAnimalsLoading ||
    studyResponse?.type !== 'success' ||
    samplePlanResponse?.type !== 'success' ||
    groupsResponse?.type !== 'success' ||
    registeredAnimalsResponse?.type !== 'success' ||
    plannedAnimalsResponse?.type !== 'success' ||
    _isNil(tableData)
  ) {
    return <Loading />;
  }

  const study = (studyResponse.body as { data: Study }).data;

  const handleEdit = () => {
    openModal('ADD_SAMPLE_PLANS', {
      studyId: study.id,
      studyApiId: study.api_id,
      onSampleDefinitionsUpdate: onSamplePlanUpdated,
      sampleDefinitions: samplePlanResponse.body.definitions,
    });
  };

  const handleExport = async () => {
    if (asyncExportsEnabled) {
      await triggerAsyncExport(() => createSamplingPlanExport({ path: { studyApiId: study.api_id } }));
      return;
    }
    await downloadSamplingPlan(study.api_id);
  };

  return (
    <div className="ui-card">
      <SamplePlanTableViewContent
        study={study}
        sampleDefinitions={samplePlanResponse.body.definitions as Array<SampleDefinitionFormData>}
        groups={tableData.groups}
        registeredAnimals={tableData.registeredAnimals}
        plannedAnimals={plannedAnimalsResponse.body.data as Array<PlannedAnimalTarget>}
        metadataGlossary={(sampleMetadataGlossaryResponse?.body ?? []) as Array<MetadataGlossaryV1>}
        sampleGlossary={
          ((sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []) as Array<TeamGlossary>
        }
        onAddSamplePlan={handleEdit}
      >
        <div className="flex flex-row ma3">
          {isWriteUserForStudy(study) && (
            <SamplePlanTableBulkActionsBar studyApiId={study.api_id} studyStatus={study.status} />
          )}
          <div className="flex flex-row items-center ml-auto">
            {isWriteUserForStudy(study) && (
              <Button plain onClick={handleEdit}>
                Edit
              </Button>
            )}
            <Button primary className="ml2" onClick={handleExport}>
              Export
            </Button>
          </div>
        </div>
      </SamplePlanTableViewContent>
    </div>
  );
};
